// Éditeur des étapes d'UN dossier : renommer, ajouter, retirer, réordonner.
// N'écrit que la copie du dossier ; le modèle des paramètres ne bouge pas.
import { useState } from 'react';
import { toast } from 'sonner';
import { useEditDossierFriseSteps } from '../../hooks/queries/useDossiers';
import type { DossierFriseStep } from '../../services/dossiers/friseService';
import {
  addStep,
  canMoveStep,
  canRemoveStep,
  FRISE_LABEL_MAX,
  FRISE_MAX_STEPS,
  isFriseEditDirty,
  moveStep,
  toFriseEditPayload,
  validateFriseEdit,
  type FriseEditRow,
} from '../../lib/dossiers/friseEdit';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface Props {
  dossierId: string;
  steps: DossierFriseStep[];
  currentPosition: number;
  onClose: () => void;
}

const ICON_BTN =
  'p-1.5 rounded hover:bg-neutral-100 disabled:opacity-25 disabled:hover:bg-transparent';

export function DossierFriseEditor({ dossierId, steps, currentPosition, onClose }: Props) {
  const edit = useEditDossierFriseSteps();
  const [rows, setRows] = useState<FriseEditRow[]>(() =>
    steps.map((step) => ({ from: step.position, label: step.label })),
  );
  const ctx = {
    currentPosition,
    lastPosition: steps[steps.length - 1]?.position ?? currentPosition,
  };
  const error = validateFriseEdit(rows, ctx);
  const dirty = isFriseEditDirty(rows, steps);

  function save() {
    if (error || !dirty) return;
    edit.mutate(
      { id: dossierId, steps: toFriseEditPayload(rows) },
      {
        onSuccess: () => {
          toast.success('Étapes du dossier enregistrées');
          onClose();
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Impossible d’enregistrer les étapes'),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !edit.isPending) onClose(); }}>
      <DialogContent className="sm:max-w-lg bg-white rounded-cime border-neutral-200">
        <DialogHeader>
          <DialogTitle className="text-cime-text-primary">Étapes de ce dossier</DialogTitle>
          <DialogDescription className="text-cime-text-muted">
            Ces changements ne valent que pour ce dossier. Le modèle des paramètres et les
            autres dossiers ne bougent pas.
          </DialogDescription>
        </DialogHeader>

        <ol className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
          {rows.map((row, index) => {
            const isCurrent = row.from === ctx.currentPosition;
            const isLast = row.from === ctx.lastPosition;
            return (
              <li key={row.from ?? `new-${index}`} className="flex items-center gap-1.5">
                <span className="w-5 text-right text-xs tabular-nums text-cime-text-muted">
                  {index + 1}
                </span>
                <input
                  value={row.label}
                  maxLength={FRISE_LABEL_MAX}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((r, i) => (i === index ? { ...r, label: e.target.value } : r)),
                    )
                  }
                  placeholder="Nom de l’étape"
                  aria-label={`Nom de l’étape ${index + 1}`}
                  className="h-9 min-w-0 flex-1 rounded-md border border-neutral-200 px-2.5 text-sm text-cime-text-primary focus:border-cime-primary focus:outline-none"
                />
                {isCurrent ? (
                  <span className="rounded-full bg-cime-gold/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-cime-gold-text">
                    En cours
                  </span>
                ) : isLast ? (
                  <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                    Fin
                  </span>
                ) : null}
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Monter l’étape"
                  title="Monter"
                  disabled={!canMoveStep(rows, index, -1, ctx)}
                  onClick={() => setRows((prev) => moveStep(prev, index, -1))}
                >
                  <img src="/icons/arrow-up.svg" alt="" className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Descendre l’étape"
                  title="Descendre"
                  disabled={!canMoveStep(rows, index, 1, ctx)}
                  onClick={() => setRows((prev) => moveStep(prev, index, 1))}
                >
                  <img src="/icons/arrow-down.svg" alt="" className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Retirer l’étape"
                  title={
                    isCurrent
                      ? 'L’étape en cours ne se retire pas'
                      : isLast
                        ? 'La dernière étape ne se retire pas'
                        : 'Retirer'
                  }
                  disabled={!canRemoveStep(row, ctx)}
                  onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                >
                  <img src="/icons/xmark.svg" alt="" className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ol>

        <div className="flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={rows.length >= FRISE_MAX_STEPS}
            onClick={() => setRows((prev) => addStep(prev))}
          >
            <img src="/icons/plus.svg" alt="" className="w-3.5 h-3.5 mr-1.5" />
            Ajouter une étape
          </Button>
          {dirty && error ? <p className="text-xs text-cime-urgent text-right">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" disabled={edit.isPending} onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            isLoading={edit.isPending}
            disabled={!dirty || !!error}
            onClick={save}
          >
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
